import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "liftblick-cookie-consent-v2";

type Consent = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  ts: number;
};

const defaultConsent: Consent = {
  necessary: true,
  functional: true,
  analytics: false,
  marketing: false,
  ts: 0,
};

function loadConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Consent;
  } catch {
    return null;
  }
}

function saveConsent(c: Consent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    (window as any).__liftblickConsent = c;
    window.dispatchEvent(new CustomEvent("cookieConsentChanged", { detail: c }));
  } catch {
    /* noop */
  }
}

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [prefs, setPrefs] = useState<Consent>(defaultConsent);

  useEffect(() => {
    const existing = loadConsent();
    if (existing) {
      (window as any).__liftblickConsent = existing;
      setPrefs(existing);
    } else {
      setVisible(true);
      setBlocking(true);
    }
    const openHandler = () => {
      const cur = loadConsent() ?? defaultConsent;
      setPrefs(cur);
      setShowSettings(true);
      setVisible(true);
      setBlocking(!loadConsent());
    };
    window.addEventListener("openCookieSettings", openHandler);
    return () => window.removeEventListener("openCookieSettings", openHandler);
  }, []);

  // Solange keine Auswahl getroffen wurde: Seite nicht bedienbar (kein Scrollen).
  useEffect(() => {
    if (!blocking) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [blocking]);

  const persistAndClose = (c: Consent) => {
    const next = { ...c, ts: Date.now() };
    saveConsent(next);
    setPrefs(next);
    setVisible(false);
    setShowSettings(false);
    setBlocking(false);
  };

  const acceptAll = () =>
    persistAndClose({ necessary: true, functional: true, analytics: true, marketing: true, ts: 0 });
  const rejectAll = () =>
    persistAndClose({ necessary: true, functional: false, analytics: false, marketing: false, ts: 0 });
  const saveCustom = () => persistAndClose(prefs);

  if (!visible) return null;

  return (
    <>
      {blocking && (
        <div
          aria-hidden
          className="fixed inset-0 z-[59] bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200"
        />
      )}
    <div
      role="dialog"
      aria-modal={blocking}
      aria-label="Cookie-Einstellungen"
      aria-live="polite"
      className={
        blocking
          ? "fixed z-[60] inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
          : "fixed z-[60] inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-w-md animate-in slide-in-from-bottom-4 duration-300"
      }
    >
      <div className="w-full sm:max-w-md bg-card border border-border shadow-lg sm:rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 sm:p-5">
          <div className="shrink-0 w-9 h-9 rounded-full bg-primary/5 flex items-center justify-center">
            <Cookie className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-foreground">Cookies auf liftblick.ch</h2>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              Wir verwenden technisch notwendige Cookies für den Betrieb der Plattform. Optionale Cookies
              helfen uns, das Angebot zu verbessern. Sie können Ihre Auswahl jederzeit ändern.{" "}
              <Link
                to="/datenschutz"
                className="text-foreground underline underline-offset-2 hover:no-underline"
              >
                Mehr erfahren
              </Link>
            </p>
          </div>
          {!blocking && (
            <button
              type="button"
              aria-label="Schliessen"
              onClick={rejectAll}
              className="shrink-0 -m-1 p-1 text-muted-foreground hover:text-foreground transition-colors sm:hidden"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Settings (inline, scrollbar) */}
        {showSettings && (
          <div className="border-t border-border max-h-[55vh] overflow-y-auto">
            <ul className="divide-y divide-border">
              <CategoryRow
                title="Notwendig"
                description="Erforderlich für Authentifizierung, Sitzung und Sicherheit. Können nicht deaktiviert werden."
                checked
                disabled
              />
              <CategoryRow
                title="Funktional"
                description="Speichert Einstellungen wie Sprache und UI-Präferenzen für eine bessere Nutzung."
                checked={prefs.functional}
                onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
              />
              <CategoryRow
                title="Analyse"
                description="Nutzungsstatistiken mit Google Analytics 4 zur Verbesserung der Plattform. Wird nur mit Ihrer Zustimmung geladen."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <CategoryRow
                title="Marketing"
                description="Personalisierte Inhalte und Reichweitenmessung. Aktuell nicht aktiv."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="border-t border-border p-3 sm:p-4 bg-muted/30">
          {showSettings ? (
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                Zurück
              </Button>
              <Button size="sm" onClick={saveCustom}>
                Auswahl speichern
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col-reverse sm:flex-row gap-2">
                <Button variant="outline" size="sm" className="sm:flex-1" onClick={rejectAll}>
                  Nur notwendige
                </Button>
                <Button size="sm" className="sm:flex-1" onClick={acceptAll}>
                  Alle akzeptieren
                </Button>
              </div>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 self-center"
              >
                Einstellungen anpassen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

const CategoryRow = ({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) => (
  <li className="flex items-start gap-3 px-4 sm:px-5 py-3.5">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">{title}</span>
        {disabled && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5">
            Immer aktiv
          </span>
        )}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{description}</p>
    </div>
    <Switch
      checked={checked}
      disabled={disabled}
      onCheckedChange={(v) => onChange?.(v)}
      aria-label={title}
    />
  </li>
);

export default CookieBanner;
